import {renderOriginalPackage} from './package-report.js';

const statuses = {found: '已關聯', missing: '尚未找到', ambiguous: '多個同名候選，請指定', unavailable: '指定版次已封存、刪除或移出專案', restricted: '無權限檢視指定版次'};
const kinds = {xref: '外參', image: '圖片', underlay: '底圖', 'revit-link': 'Revit 連結', 'cad-link': 'CAD 連結', font: '字型'};
function element(parent, tag, text, className) {
  const node = document.createElement(tag); if (text !== undefined) node.textContent = text;
  if (className) node.className = className; parent.append(node); return node;
}

export async function openReferencePanel(item, {fmtBytes, onChange}) {
  const dialog = document.createElement('dialog'); dialog.className = 'package-dialog reference-dialog';
  const header = element(dialog, 'header', undefined, 'package-dialog-head');
  const title = element(header, 'div', undefined, 'reference-title');
  element(title, 'h2', '外參關聯');
  element(title, 'p', item.name, 'reference-file');
  const close = element(header, 'button', '✕', 'icon-btn package-dialog-close');
  close.type = 'button'; close.dataset.close = ''; close.setAttribute('aria-label', '關閉'); close.onclick = () => dialog.close();
  const toolbar = element(dialog, 'div', undefined, 'reference-toolbar');
  const filters = element(toolbar, 'div', undefined, 'reference-filters');
  const filterAll = element(filters, 'button', '全部', 'btn btn-sm reference-filter');
  const filterPending = element(filters, 'button', '待確認', 'btn btn-sm reference-filter');
  filterAll.type = 'button'; filterPending.type = 'button';
  const counts = element(filters, 'span', '', 'reference-counts');
  const search = element(toolbar, 'input');
  search.type = 'search'; search.placeholder = '篩選引用路徑或關聯檔案'; search.setAttribute('aria-label', '篩選外參');
  const notice = element(dialog, 'p', '讀取外參關聯…', 'reference-status'); notice.setAttribute('role', 'status'); notice.dataset.tone = 'busy';
  function setNotice(text, tone = '') { notice.textContent = text; notice.dataset.tone = text ? tone : ''; }
  const body = element(dialog, 'div', undefined, 'package-dialog-body');
  const content = element(body, 'div');
  const none = element(body, 'p', '沒有符合的外參。', 'reference-none'); none.hidden = true;
  let filterMode = 'all';
  let scanning = false;
  function applyView() {
    const query = search.value.trim().toLowerCase();
    const rows = [...content.querySelectorAll('.reference-row')];
    for (const row of rows) {
      const pending = row.dataset.status !== 'found';
      row.hidden = (filterMode === 'pending' && !pending) || (query && !(row.dataset.filterText || '').includes(query));
    }
    for (const section of content.querySelectorAll('[data-reference-owner]')) {
      const owned = [...section.querySelectorAll('.reference-row')];
      section.hidden = owned.length > 0 && owned.every(row => row.hidden);
    }
    none.hidden = !rows.length || rows.some(row => !row.hidden);
  }
  function setFilter(mode) {
    filterMode = mode;
    filterAll.setAttribute('aria-pressed', mode === 'all' ? 'true' : 'false');
    filterPending.setAttribute('aria-pressed', mode === 'pending' ? 'true' : 'false');
    applyView();
  }
  filterAll.onclick = () => setFilter('all');
  filterPending.onclick = () => setFilter('pending');
  search.oninput = () => applyView();
  setFilter('all');
  dialog.addEventListener('close', () => dialog.remove());
  document.body.append(dialog); dialog.showModal();
  async function request(owner, options = {}, query = '') {
    const response = await fetch(`/api/library/${owner}/references${query}`, {cache:'no-store',...options});
    const value = await response.json();
    if (!response.ok) throw new Error(value.error || '外參關聯讀取失敗');
    return {value, etag: response.headers.get('X-DCMS-Version') || response.headers.get('ETag')};
  }
  async function change(owner, data, etag) {
    if (data.operation !== 'rescan') setNotice('儲存外參關聯…', 'busy');
    try {
      await request(owner, {method: 'PATCH', headers: {'Content-Type': 'application/json', 'If-Match': etag}, body: JSON.stringify(data)});
      if (!dialog.isConnected) return;
      await load();
      setNotice(data.operation === 'rescan' ? '已重新掃描這個檔案。' : '關聯已更新，受影響圖面的檢視結果將重新產生。', 'ok');
      Promise.resolve(onChange?.()).catch(error => { if (dialog.isConnected) setNotice(`關聯已儲存；檢視更新失敗：${error.message}`, 'err'); });
    } catch (error) { setNotice(error.message, 'err'); }
  }
  async function choose(parent, owner, ref) {
    parent.querySelector('.reference-picker')?.remove();
    const form = element(parent, 'div', undefined, 'reference-picker');
    const label = element(form, 'label', '搜尋同專案文件與版次');
    const field = element(label, 'input'); field.type = 'search'; field.placeholder = '檔名或目錄，可指定不同檔名';
    const select = element(form, 'select'); select.setAttribute('aria-label', '外參文件與版次');
    const actions = element(form, 'div', undefined, 'reference-picker-actions');
    const save = element(actions, 'button', '確認關聯', 'btn btn-sm btn-primary'); save.type = 'button'; save.disabled = true;
    const cancel = element(actions, 'button', '取消', 'btn btn-sm'); cancel.type = 'button'; cancel.onclick = () => form.remove();
    const status = element(form, 'p'); let etag, sequence = 0, timer;
    const reload = async () => {
      const token = ++sequence;
      try {
        const result = await request(owner, {}, `?key=${encodeURIComponent(ref.key)}&q=${encodeURIComponent(field.value)}`);
        if (token !== sequence || !form.isConnected) return;
        etag = result.etag; select.replaceChildren();
        select.append(new Option('請選擇文件及版次', ''));
        for (const candidate of result.value.candidates) select.append(new Option(`${candidate.path} · ${candidate.revision || '未標版次'}${candidate.current === null ? '（來源附件）' : candidate.current ? '（現行）' : '（歷史）'}`, candidate.id));
        if (ref.target?.id) select.value = ref.target.id;
        save.disabled = !select.value;
        status.textContent = result.value.candidates.length ? `顯示 ${result.value.candidates.length} 筆，最多 200 筆。` : '沒有可使用的同專案文件；請先上傳，或確認檔案格式與權限。';
      } catch (error) { status.textContent = error.message; }
    };
    select.onchange = () => { save.disabled = !select.value; };
    field.oninput = () => { sequence++; save.disabled = true; clearTimeout(timer); timer = setTimeout(reload, 250); };
    save.onclick = () => { save.disabled = true; change(owner, {operation: 'bind', key: ref.key, targetId: select.value}, etag); };
    await reload(); field.focus();
  }
  async function load() {
    const {value: report} = await request(item.id);
    if (!dialog.isConnected) return;
    content.replaceChildren();
    element(content, 'p', '目前開啟的圖是主檔。每一層外參都可以收合，下一層會跟著該筆外參收起。重新掃描只重讀該層這一個檔案。', 'reference-note');
    const names = new Map(report.nodes.map(node => [node.id, node.name]));
    const drawingNodes = report.nodes.filter(node => /\.(dwg|dxf|rvt|rfa)$/i.test(node.name));
    const childrenByParent = new Map();
    const childIds = new Set();
    for (const node of drawingNodes) {
      if (node.chain.length < 2) continue;
      const parentId = node.chain[node.chain.length - 2];
      childIds.add(node.id);
      const list = childrenByParent.get(parentId) || [];
      list.push(node);
      childrenByParent.set(parentId, list);
    }
    const drawingRefs = [];
    const placed = new Set();
    function renderNode(node, container, depth) {
      const section = element(container, 'section', undefined, depth ? 'reference-branch' : 'reference-root');
      section.dataset.referenceOwner = node.itemId || node.id;
      section.dataset.depth = String(depth);
      const head = element(section, 'div', undefined, 'reference-owner-head');
      const heading = element(head, 'div', undefined, 'reference-owner-name');
      const titleRow = element(heading, 'div', undefined, 'reference-level-line');
      element(titleRow, 'span', depth === 0 ? '主檔' : `第 ${depth + 1} 層`, 'reference-level');
      element(titleRow, 'h3', node.path);
      if (node.chain.length > 1) element(heading, 'p', `引用鏈：${node.chain.map(id => names.get(id) || '歷史引用').join(' → ')}`, 'package-chain');
      if (node.canEdit) {
        const scan = element(head, 'button', '重新掃描', 'btn btn-sm'); scan.type = 'button';
        scan.title = '只重新讀取這個檔案的引用定義';
        scan.onclick = async () => {
          if (scanning) return;
          scanning = true;
          const buttons = [...content.querySelectorAll('.reference-owner-head button')];
          buttons.forEach(button => { button.disabled = true; });
          scan.classList.add('reference-scanning');
          scan.textContent = '掃描中';
          scan.setAttribute('aria-busy', 'true');
          const started = Date.now();
          const showProgress = () => setNotice(`正在重新掃描「${node.name}」… 已等候 ${Math.floor((Date.now() - started) / 1000)} 秒`, 'busy');
          showProgress();
          const timer = setInterval(showProgress, 1000);
          try {
            const latest = await request(node.itemId);
            await change(node.itemId, {operation: 'rescan'}, latest.etag);
          } catch(error) {
            if (dialog.isConnected) setNotice(error.message, 'err');
          } finally {
            clearInterval(timer);
            scanning = false;
            buttons.forEach(button => { button.disabled = false; });
            scan.classList.remove('reference-scanning');
            scan.removeAttribute('aria-busy');
            scan.textContent = '重新掃描';
          }
        };
      }
      if (node.status !== 'complete') element(section, 'p', node.status === 'error' ? `掃描失敗：${node.error || '原因未記錄'}` : '外參掃描中，尚無完整結果。', 'reference-note');
      const rows = node.references.filter(ref => !ref.font).sort((a,b) => Number(a.status === 'found') - Number(b.status === 'found'));
      drawingRefs.push(...rows);
      if (!rows.length && node.status === 'complete') element(section, 'p', '沒有外參、圖片或底圖引用。', 'reference-note');
      const kids = childrenByParent.get(node.id) || [];
      for (const ref of rows) {
        const row = element(section, 'details', undefined, 'reference-row'); row.open = true; row.dataset.referenceKey = ref.key; row.dataset.status = ref.status || 'missing';
        const summary = element(row, 'summary', undefined, 'reference-main');
        summary.title = '收合或展開這個外參';
        element(summary, 'span', kinds[ref.kind] || ref.kind, 'reference-kind');
        element(summary, 'strong', ref.path);
        const pill = element(summary, 'span', statuses[ref.status] || '待確認', 'reference-pill'); pill.dataset.state = ref.status || 'missing';
        const body = element(row, 'div', undefined, 'reference-body');
        const targetText = ref.target ? `${ref.target.path} · 版次 ${ref.target.revision || '未記錄'} · ${ref.mode === 'manual' ? '手動指定' : '自動配對'}` : '';
        if (targetText) element(body, 'p', targetText, 'reference-target');
        if (ref.assignedBy) element(body, 'p', `指定者：${ref.assignedBy}${ref.assignedAt ? ` · ${new Date(ref.assignedAt).toLocaleString()}` : ''}`, 'reference-meta');
        if (ref.updateAvailable) element(body, 'p', '此文件已有其他現行版；目前保留原關聯版次。', 'reference-update');
        row.dataset.filterText = `${ref.path} ${ref.target?.path || ''} ${statuses[ref.status] || ''}`.toLowerCase();
        if (node.canEdit) {
          const actions = element(body, 'div', undefined, 'reference-actions');
          const bind = element(actions, 'button', ref.target ? '重新指定' : '指定檔案', 'btn btn-sm'); bind.type = 'button';
          bind.onclick = () => choose(body, node.itemId, ref);
          if (ref.mode === 'manual') {
            const auto = element(actions, 'button', '恢復自動配對', 'btn btn-sm'); auto.type = 'button';
            auto.onclick = async () => { try { const latest = await request(node.itemId); await change(node.itemId, {operation:'auto',key:ref.key}, latest.etag); } catch(error) { setNotice(error.message, 'err'); } };
          }
        }
        const matched = kids.filter(child => !placed.has(child.id) && (child.id === ref.target?.id || child.id === ref.target?.itemId || child.itemId && child.itemId === ref.target?.itemId));
        if (matched.length) {
          const nest = element(body, 'div', undefined, 'reference-nest');
          for (const child of matched) { placed.add(child.id); renderNode(child, nest, depth + 1); }
        }
      }
      const rest = kids.filter(child => !placed.has(child.id));
      if (rest.length) {
        const nest = element(section, 'div', undefined, 'reference-nest');
        for (const child of rest) { placed.add(child.id); renderNode(child, nest, depth + 1); }
      }
      const fonts = node.references.filter(ref => ref.font);
      if (fonts.length) {
        const details = element(section, 'details', undefined, 'package-fonts'); element(details, 'summary', `字型（${fonts.length}）`);
        const list = element(details, 'ul');
        for (const font of fonts) element(list, 'li', `${font.path} — ${font.status === 'found' ? '可用' : '待確認'}`);
      }
    }
    const roots = drawingNodes.filter(node => !childIds.has(node.id));
    roots.sort((a, b) => Number(b.itemId === item.id) - Number(a.itemId === item.id) || a.chain.length - b.chain.length);
    for (const node of roots) renderNode(node, content, 0);
    const pendingCount = drawingRefs.filter(ref => ref.status !== 'found').length;
    filterAll.textContent = `全部 ${drawingRefs.length}`;
    filterPending.textContent = `待確認 ${pendingCount}`;
    counts.textContent = `已關聯 ${drawingRefs.length - pendingCount}`;
    if (report.package?.canRead) {
      const full = element(content, 'details', undefined, 'package-full'); element(full, 'summary', '查看原始 ZIP 套件');
      const host = element(full, 'div');
      full.addEventListener('toggle', async () => {
        if (!full.open || full.dataset.loaded) return;
        full.dataset.loaded = '1';
        try {
          const response = await fetch(`/api/library/${item.id}/package`); const data = await response.json();
          if (!response.ok) throw new Error(data.error || '無法查看完整套件');
          renderOriginalPackage(host, data.package, item, fmtBytes);
        } catch (error) { host.textContent = error.message; }
      });
    }
    if (notice.dataset.tone === 'busy' && notice.textContent === '讀取外參關聯…') setNotice('');
    applyView();
  }
  try { await load(); } catch (error) { setNotice(error.message, 'err'); }
  return dialog;
}
